import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllItems } from '../utils/itemOperations';
import Layout from '../components/layout/Layout';
import BatchReservationModal from '../components/reservations/BatchReservationModal';

const Items = () => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    category: ''
  });
  const [categories, setCategories] = useState([]);
  const [showBatchReservation, setShowBatchReservation] = useState(false);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        setLoading(true);
        const fetchedItems = await getAllItems();
        setItems(fetchedItems);
        setFilteredItems(fetchedItems);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(fetchedItems.map(item => item.category).filter(Boolean))];
        setCategories(uniqueCategories);
      } catch (error) {
        console.error('Error fetching items:', error);
        setError('Failed to load items. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  useEffect(() => {
    // Apply filters and search
    let results = [...items];
    
    // Apply category filter
    if (filters.category) {
      results = results.filter(item => item.category === filters.category);
    }
    
    // Apply status filter
    if (filters.status) {
      if (filters.status === 'Low Stock') {
        // Filter for consumables with low stock
        results = results.filter(item => 
          item.itemType === 'consumable' && 
          item.lowStockAlert && 
          (item.currentStock || 0) <= item.lowStockAlert
        );
      } else {
        results = results.filter(item => item.status === filters.status);
      }
    }
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      results = results.filter(item => 
        item.name.toLowerCase().includes(term) || 
        (item.description && item.description.toLowerCase().includes(term))
      );
    }
    
    setFilteredItems(results);
  }, [items, filters, searchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const clearFilters = () => {
    setFilters({
      status: '',
      category: ''
    });
    setSearchTerm('');
  };

  const handleBatchReservationSuccess = (reservations) => {
    setShowBatchReservation(false);
    console.log('Batch reservations created:', reservations);
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <div className="mb-6 bg-white rounded-lg shadow-md p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
            <h1 className="text-2xl font-bold text-gray-800">Browse Lab Items</h1>
            {/* Batch Reserve button - disabled but kept for potential reactivation */}
            {false && (
              <button
                onClick={() => setShowBatchReservation(true)}
                className="mt-4 md:mt-0 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm font-medium"
              >
                Batch Reserve
              </button>
            )}
          </div>
          
          {/* Search and Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <div className="md:w-1/2">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search Items
              </label>
              <input
                type="text"
                id="search"
                name="search"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by name or description..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-white text-black"
              />
            </div>
            
            <div className="md:w-1/4">
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                id="category"
                name="category"
                value={filters.category}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-white text-black"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            
            <div className="md:w-1/4">
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-yellow-500 focus:border-yellow-500 bg-white text-black"
              >
                <option value="">All Statuses</option>
                <option value="Available">Available</option>
                <option value="Checked Out">Checked Out</option>
                <option value="Under Maintenance">Under Maintenance</option>
                <option value="Low Stock">Low Stock (Consumables)</option>
              </select>
            </div>
          </div>
          
          {/* Applied Filters */}
          {(filters.category || filters.status || searchTerm) && (
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm text-gray-600">Filters:</span>
              {filters.category && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Category: {filters.category}
                </span>
              )}
              {filters.status && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Status: {filters.status}
                </span>
              )}
              {searchTerm && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  Search: "{searchTerm}"
                </span>
              )}
              <button
                onClick={clearFilters}
                className="ml-2 text-sm text-red-600 hover:text-red-800"
              >
                Clear All
              </button>
            </div>
          )}
        </div>
        
        {/* Items Grid */}
        {error && (
          <div className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-md mb-6">
            {error}
          </div>
        )}
        
        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading items...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <div className="flex flex-col items-center">
              <svg
                className="h-12 w-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-lg font-medium text-gray-900">No items found</h3>
              <p className="mt-1 text-gray-500">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map(item => (
              <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="h-48 relative">
                  {item.imageUrl ? (
                    <img 
                      src={item.imageUrl} 
                      alt={item.name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-500">No image</span>
                    </div>
                  )}
                  <div className="absolute top-2 right-2">
                    <span className={`py-1 px-2 rounded-full text-xs font-bold ${
                      item.status === 'Available' 
                        ? 'bg-green-100 text-green-800' 
                        : item.status === 'Checked Out' 
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {item.status}
                    </span>
                  </div>
                </div>
                
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{item.name}</h3>
                  <p className="text-sm text-gray-500 mb-2">
                    {item.category || 'Uncategorized'}
                  </p>
                  <p className="text-sm text-gray-700 line-clamp-2 mb-4">
                    {item.description || 'No description available.'}
                  </p>
                  
                  {/* Consumable Stock Information */}
                  {item.itemType === 'consumable' && (
                    <div className="mb-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-gray-600">Stock Level</span>
                        <span className="text-xs font-medium text-gray-900">
                          {item.currentStock || 0} {item.unit}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            item.initialStock && item.currentStock 
                              ? (item.currentStock / item.initialStock) > 0.5 ? 'bg-green-500' :
                                (item.currentStock / item.initialStock) > 0.2 ? 'bg-yellow-500' : 'bg-red-500'
                              : 'bg-gray-300'
                          }`}
                          style={{ 
                            width: item.initialStock && item.currentStock 
                              ? `${Math.max((item.currentStock / item.initialStock) * 100, 2)}%` 
                              : '2%' 
                          }}
                        ></div>
                      </div>
                      {item.lowStockAlert && item.currentStock <= item.lowStockAlert && (
                        <div className="text-xs text-red-600 mt-1 font-medium">
                          ⚠️ Low Stock Alert
                        </div>
                      )}
                    </div>
                  )}
                  
                  {item.status === 'Checked Out' && item.itemType !== 'consumable' && (
                    <p className="text-xs text-gray-600 mb-2">
                      Currently held by: {item.currentHolderName || 'Unknown'}
                    </p>
                  )}
                  
                  <Link
                    to={`/item/${item.id}`}
                    className="inline-block w-full py-2 px-4 bg-blue-600 text-white text-center rounded-md hover:bg-blue-700 transition-colors"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Batch Reservation Modal - disabled but kept for potential reactivation */}
        {false && (
          <BatchReservationModal
            isOpen={showBatchReservation}
            onClose={() => setShowBatchReservation(false)}
            onSuccess={handleBatchReservationSuccess}
          />
        )}
      </div>
    </Layout>
  );
};

export default Items;
